import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CFG = {
    vert: { label: 'Situation maîtrisée', dot: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/60', badge: 'bg-emerald-500/15 text-emerald-400' },
    jaune: { label: 'Vigilance', dot: 'bg-yellow-400', text: 'text-yellow-300', border: 'border-yellow-400/60', badge: 'bg-yellow-400/15 text-yellow-300' },
    orange: { label: 'Tension', dot: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/60', badge: 'bg-orange-500/15 text-orange-400' },
    rouge: { label: 'Critique', dot: 'bg-red-500', text: 'text-red-400', border: 'border-red-500/60', badge: 'bg-red-500/15 text-red-400' },
};

const EtatGeneral = ({ token, externalData }) => {
    const [fetched, setFetched] = useState(null);
    const [open, setOpen] = useState(false);

    const fetchEtat = useCallback(async () => {
        if (externalData) return;
        try {
            const res = await axios.get(`${API}/dashboard/etat-general`, { headers: { Authorization: `Bearer ${token}` } });
            setFetched(res.data);
        } catch { /* silencieux : le bandeau reste sur le dernier etat connu */ }
    }, [token, externalData]);

    useEffect(() => {
        if (externalData) return;
        fetchEtat();
        const interval = setInterval(fetchEtat, 30000);
        return () => clearInterval(interval);
    }, [fetchEtat, externalData]);

    const data = externalData || fetched;
    if (!data) return null;
    const cfg = CFG[data.overall] || CFG.vert;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="etat-general-banner"
                className={`glass-panel px-8 py-3 rounded-lg border-2 ${cfg.border} hover:scale-[1.02] transition-transform text-center cursor-pointer`}
                title="Cliquer pour le détail par domaine"
            >
                <p className="text-[10px] text-zinc-400 uppercase tracking-[0.25em] font-semibold">État Général</p>
                <div className="flex items-center justify-center gap-3 mt-1">
                    <span className={`w-4 h-4 rounded-full ${cfg.dot} animate-pulse flex-shrink-0`}></span>
                    <span className={`text-2xl font-bold leading-tight ${cfg.text}`} data-testid="etat-general-label">{cfg.label}</span>
                </div>
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="etat-general-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Activity className={`w-5 h-5 ${cfg.text}`} /> État Général —{' '}
                            <span className={cfg.text}>{cfg.label}</span>
                        </DialogTitle>
                        <DialogDescription>
                            Le domaine le plus dégradé détermine l'état général. Actualisé toutes les 30 secondes.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {data.domains.map(d => {
                            const dc = CFG[d.color] || CFG.vert;
                            return (
                                <div key={d.id} className={`p-3 rounded-lg border ${dc.border} bg-zinc-800/40`} data-testid={`etat-domain-${d.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-3 h-3 rounded-full ${dc.dot} flex-shrink-0`}></span>
                                            <span className="font-semibold text-white text-sm">{d.label}</span>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${dc.badge}`}>{dc.label}</span>
                                    </div>
                                    <ul className="mt-1.5 ml-5 space-y-0.5">
                                        {d.details.map(detail => (
                                            <li key={detail} className="text-xs text-zinc-400 list-disc list-inside">{detail}</li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EtatGeneral;
